// VOY-417 — shared correction modal.
//
// Opens for the §3 per-attribute Correct and §8 Memory Editor Correct/Pin
// flows. Lets the user enter a corrected value + optional reason for the
// tombstone, then writes through useFactCorrection's `correct`/`pin`.
//
// The modal is intentionally minimal — single text input + a reason box.
// Categorical attributes (e.g. accommodation: budget/mid-range/luxury) get
// a select instead via the `options` prop. The "Pin instead" button maps
// to /memory/pin (lock without correcting).

import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react'
import { Loader2 } from 'lucide-react'
import { asString } from './utils'

export interface CorrectionModalOption {
  value: string
  label: string
}

export interface CorrectionModalProps {
  open: boolean
  onClose: () => void
  /** Dimension being corrected (e.g. 'accommodation'). Used in the heading. */
  dimension: string
  /** Human label for the dimension (e.g. "Hotel tier"). */
  dimensionLabel: string
  /** Current inferred value, displayed read-only as context. */
  currentValue: unknown
  /** When provided, the value input renders as a `<select>`. */
  options?: CorrectionModalOption[]
  /** When true, the modal calls `onCorrect` and shows a "Saving" state. */
  saving: boolean
  /** Server error, surfaced as an inline alert. */
  error: string | null
  /** Fires when the user submits Correct — supplies new value + reason. */
  onCorrect: (newValue: string, reason: string) => void
  /** Fires when the user clicks "Pin instead" — locks the inferred value as-is. */
  onPin: () => void
}

export function CorrectionModal({
  open,
  onClose,
  dimension,
  dimensionLabel,
  currentValue,
  options,
  saving,
  error,
  onCorrect,
  onPin,
}: CorrectionModalProps) {
  const [newValue, setNewValue] = useState<string>('')
  const [reason, setReason] = useState<string>('')
  const dialogRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (open) {
      // Initialise with the current value so the user can tweak it.
      // Don't bias the reason — that should be the user's own framing.
      // Note: `currentValue === null` in agent-context means "we haven't
      // inferred this yet" — `asString` returns "—" which is a fine
      // placeholder for the read-only display, but we don't want that
      // glyph as the initial editable value. Coerce to "" in that case.
      setNewValue(currentValue == null ? '' : asString(currentValue))
      setReason('')
    }
  }, [open, currentValue])

  if (!open) return null

  // Empty value is invalid for both free-text and select. Bot review #2:
  // the prior guard exempted `options` (categorical select), which let
  // the user submit "" and send a blank to the backend.
  const valueIsEmpty = !newValue.trim()

  function submit(e: FormEvent) {
    e.preventDefault()
    if (valueIsEmpty) return
    onCorrect(newValue.trim(), reason.trim())
  }

  // Focus trap — Tab and Shift+Tab cycle within the dialog so screen
  // readers and keyboard users can't tab out into the page behind the
  // modal. Bot review #5.
  function trapTab(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'Tab') return
    const root = dialogRef.current
    if (!root) return
    const focusables = root.querySelectorAll<HTMLElement>(
      'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])',
    )
    if (focusables.length === 0) return
    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    const active = document.activeElement as HTMLElement | null
    if (e.shiftKey && active === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && active === last) {
      e.preventDefault()
      first.focus()
    }
  }

  return (
    // Bot review #6: this is a non-critical form, not an immediate
    // response gate — role="dialog" + labelledby/describedby is the
    // correct ARIA pattern. The destructive PrivacyControls modal
    // remains role="alertdialog" because it actually gates a critical
    // action (account deletion).
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="you-modal-title"
      aria-describedby="you-modal-blurb"
      className="you-modal-backdrop"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose()
        // Run the Tab trap on the backdrop's keydown — events bubble up
        // from focused descendants, so this catches every Tab regardless
        // of which control has focus.
        trapTab(e)
      }}
    >
      <div
        ref={dialogRef}
        className="you-modal"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <h4 id="you-modal-title" className="you-modal-title">Correct {dimensionLabel}</h4>
        <p id="you-modal-blurb" className="you-modal-blurb">
          Voy currently thinks <code className="you-modal-current">{asString(currentValue)}</code>.
          What's right?
        </p>

        <form onSubmit={submit} className="stack" style={{ gap: 12 }}>
          <label className="field">
            <span className="field-l">CORRECT VALUE</span>
            {options ? (
              <select
                autoFocus
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                disabled={saving}
              >
                <option value="">—</option>
                {options.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            ) : (
              <input
                autoFocus
                type="text"
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                disabled={saving}
                placeholder={`e.g. the value you actually prefer for ${dimensionLabel.toLowerCase()}`}
              />
            )}
          </label>

          <label className="field">
            <span className="field-l">REASON (OPTIONAL)</span>
            <textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={saving}
              placeholder="What did Voy get wrong?"
              maxLength={500}
              style={{ resize: 'vertical', fontFamily: 'inherit' }}
            />
          </label>

          {error && (
            <p style={{ color: 'var(--v6-neg)', fontSize: 12, margin: 0 }} role="alert">{error}</p>
          )}

          <div className="you-modal-actions">
            <button type="button" className="btn-ghost" style={{ padding: '6px 12px', fontSize: 11.5 }} onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="button" className="btn-ghost" style={{ padding: '6px 12px', fontSize: 11.5 }} onClick={onPin} disabled={saving}>
              Pin instead
            </button>
            <button
              type="submit"
              className="btn-primary"
              style={{ padding: '6px 12px', fontSize: 11.5, display: 'inline-flex', alignItems: 'center', gap: 6 }}
              disabled={saving || valueIsEmpty}
            >
              {saving && <Loader2 className="kw-spin" aria-hidden="true" />}
              {saving ? 'Saving…' : 'Save correction'}
            </button>
          </div>
        </form>

        <p className="you-modal-fine">
          Dimension <code>{dimension}</code> · A correction outranks the agent's inference and is remembered next time.
        </p>
      </div>
    </div>
  )
}
